import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea
} from 'recharts';
import axios from 'axios';
import moment from 'moment';
import 'moment-timezone';
import Loader from '../Loader/Loader';
import { useGlucoseTargets } from '../../hooks/useGlucoseTargets';
import './DailyGlucoseProfiles.css';

const base_url = process.env.REACT_APP_BASE_URL;

// Helper: Convert UTC timestamp to local moment
const toLocalTime = (timestamp) => moment.utc(timestamp).local();

// Helper: Get UTC date string for start of a local day
const getUTCDateForLocalDayStart = (localDate) => {
  return moment(localDate).startOf('day').utc().format('YYYY-MM-DD');
};

// Helper: Get UTC date string for end of a local day  
const getUTCDateForLocalDayEnd = (localDate) => {
  return moment(localDate).endOf('day').utc().format('YYYY-MM-DD');
};

function DailyGlucoseProfiles() {
  const DATE = localStorage.getItem("DATE");
  const UID = localStorage.getItem('UID')?.replace(/['"]+/g, '');
  const user = localStorage.getItem("user")?.replace(/['"]+/g, '');
  const [userEmail, setUserEmail] = useState(null);
  const { lower_range: targetLower, upper_range: targetUpper } = useGlucoseTargets(UID);
  
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState([]);

  // Watch for email changes in localStorage
  useEffect(() => {
    const checkEmail = () => {
      const currentEmail = localStorage.getItem("email");
      if (currentEmail && currentEmail !== userEmail) {
        setUserEmail(currentEmail);
      }
    };
    checkEmail();
    const interval = setInterval(checkEmail, 300);
    return () => clearInterval(interval);
  }, [userEmail]);

  // Fetch daily glucose data for the week
  const fetchDailyData = async () => {
    if (!UID || !userEmail) return;
    
    setLoading(true);
    
    // Get the week based on DATE
    const weekStart = moment(DATE).startOf('isoWeek');
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    
    try {
      // Fetch data for each day of the week
      const weekData = await Promise.all(
        daysOfWeek.map(async (dayName, index) => {
          const dayDate = moment(weekStart).add(index, 'days');
          const dateStr = dayDate.format('YYYY-MM-DD');
          
          try {
            // Calculate UTC date range that covers the full local day
            // This accounts for timezone offset (e.g., EST readings after 7PM would be next day UTC)
            const utcStartDate = getUTCDateForLocalDayStart(dayDate);
            const utcEndDate = getUTCDateForLocalDayEnd(dayDate);
            
            const response = await axios.post(
              base_url + '/admin/influxdb/glucose-data',
              {
                uuid: UID,
                email: userEmail,
                start_date: utcStartDate,
                end_date: utcEndDate,
                is_admin: 'True'
              },
              { headers: { "Authorization": `Bearer ${user}` } }
            );
            
            if (response.data.response !== "Error while fetching data") {
              const rawData = response.data.response?.cgm_data || [];
              
              // Define local day boundaries for filtering
              const localDayStart = moment(dayDate).startOf('day');
              const localDayEnd = moment(dayDate).endOf('day');
              
              // Process data into hourly format for the chart
              const hourlyData = [];
              for (let hour = 0; hour < 24; hour++) {
                const hourReadings = rawData.filter(reading => {
                  const timestamp = reading.timestamp || reading.time || reading._time;
                  if (!timestamp) return false;
                  
                  // Convert UTC timestamp to local time
                  const localTime = toLocalTime(timestamp);
                  
                  // Ensure reading falls within the local day (handles timezone edge cases)
                  if (localTime.isBefore(localDayStart) || localTime.isAfter(localDayEnd)) {
                    return false;
                  }
                  
                  return localTime.hour() === hour;
                });
                
                if (hourReadings.length > 0) {
                  const validReadings = hourReadings.filter(r => {
                    const val = r.glucose || r.value;
                    return val >= 36 && val <= 450;
                  });
                  
                  if (validReadings.length === 0) {
                    hourlyData.push({
                      hour: `${hour.toString().padStart(2, '0')}:00`,
                      glucose: null,
                      above: null,
                      below: null,
                      inRange: null
                    });
                    continue;
                  }
                  
                  const avgGlucose = validReadings.reduce((sum, r) => {
                    return sum + (r.glucose || r.value || 0);
                  }, 0) / validReadings.length;
                  
                  hourlyData.push({
                    hour: `${hour.toString().padStart(2, '0')}:00`,
                    glucose: Math.round(avgGlucose * 10) / 10,
                    // For highlighting above/below range
                    above: avgGlucose > targetUpper ? avgGlucose : null,
                    below: avgGlucose < targetLower ? avgGlucose : null,
                    inRange: avgGlucose >= targetLower && avgGlucose <= targetUpper ? avgGlucose : null
                  });
                } else {
                  hourlyData.push({
                    hour: `${hour.toString().padStart(2, '0')}:00`,
                    glucose: null,
                    above: null,
                    below: null,
                    inRange: null
                  });
                }
              }
              
              return {
                dayName: dayName.slice(0, 3), // Mon, Tue, etc.
                date: dayDate.format('D'),
                fullDate: dateStr,
                data: hourlyData,
                hasData: hourlyData.some(d => d.glucose !== null)
              };
            }
            
            return {
              dayName: dayName.slice(0, 3),
              date: dayDate.format('D'),
              fullDate: dateStr,
              data: [],
              hasData: false
            };
          } catch (error) {
            console.error(`Error fetching data for ${dateStr}:`, error);
            return {
              dayName: dayName.slice(0, 3),
              date: dayDate.format('D'),
              fullDate: dateStr,
              data: [],
              hasData: false
            };
          }
        })
      );
      
      setDailyData(weekData);
    } catch (error) {
      console.error('Error fetching daily glucose data:', error);
      setDailyData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userEmail && UID) {
      fetchDailyData();
    }
  }, [userEmail, UID, DATE, targetLower, targetUpper]);

  // Render a single day's chart
  const renderDayChart = (dayData) => {
    const { dayName, date, data, hasData } = dayData;
    
    return (
      <div key={dayData.fullDate} className="daily-chart-item">
        <div className="daily-chart-header">
          <span className="daily-chart-date">{date}</span>
          <span className="daily-chart-day">{dayName}</span>
        </div>
        <div className="daily-chart-content">
          {hasData ? (
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
                {/* Target range background */}
                <ReferenceArea y1={targetLower} y2={targetUpper} fill="#4CAF50" fillOpacity={0.15} />
                
                <XAxis 
                  dataKey="hour" 
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 8, fill: '#999' }}
                  interval={5}
                  tickFormatter={(val) => val.split(':')[0]}
                />
                <YAxis 
                  domain={[0, 270]}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 8, fill: '#999' }}
                  ticks={[targetLower, targetUpper]}
                  width={30}
                />
                
                {/* Reference lines for target range */}
                <ReferenceLine y={targetLower} stroke="#4CAF50" strokeWidth={1} strokeDasharray="2 2" />
                <ReferenceLine y={targetUpper} stroke="#E53935" strokeWidth={1} strokeDasharray="2 2" />
                
                {/* High glucose area (yellow) */}
                <Area
                  type="monotone"
                  dataKey="glucose"
                  stroke="none"
                  fill="#FFD54F"
                  fillOpacity={0.6}
                  baseValue={targetUpper}
                  connectNulls
                />
                
                {/* Main glucose line */}
                <Line
                  type="monotone"
                  dataKey="glucose"
                  stroke="#1a237e"
                  strokeWidth={1.5}
                  dot={false}
                  connectNulls
                />
                
                <Tooltip
                  formatter={(value) => value ? [`${value} mg/dL`, 'Glucose'] : ['N/A', 'Glucose']}
                  labelFormatter={(label) => label}
                  contentStyle={{ fontSize: 10, padding: '4px 8px' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="daily-chart-no-data">No data</div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className='col-wrapper exercise full-width-blk daily-profiles-wrapper'>
        <div className='filter-wrapper'>
          <div className='left-div'>
            <div className='total-cal dark-text'>Daily Glucose Profiles</div>
          </div>
        </div>
        <Loader />
      </div>
    );
  }

  return (
    <div className='col-wrapper exercise full-width-blk daily-profiles-wrapper'>
      <div className='filter-wrapper'>
        <div className='left-div'>
          <div className='total-cal dark-text'>Daily Glucose Profiles</div>
          <div className='daily-profiles-subtitle'>
            Each profile shows midnight to midnight glucose pattern
          </div>
        </div>
      </div>
      
      {/* Legend */}
      <div className="daily-profiles-legend">
        <div className="legend-item">
          <span className="legend-line"></span>
          <span>Glucose</span>
        </div>
        <div className="legend-item">
          <span className="legend-box target"></span>
          <span>Target Range ({targetLower}-{targetUpper})</span>
        </div>
        <div className="legend-item">
          <span className="legend-box high"></span>
          <span>Above Range</span>
        </div>
      </div>

      {/* Daily charts grid */}
      <div className="daily-charts-grid">
        {dailyData.map(dayData => renderDayChart(dayData))}
      </div>
      
      <div className="daily-profiles-footer">
        <span>Time: 00:00 - 24:00 (midnight to midnight)</span>
      </div>
    </div>
  );
}

export default DailyGlucoseProfiles;

